import React, { ReactNode, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { preventOverflow } from '../../../web3/utils';
import useErrorState from '../../../context/error/useErrorState';
import useModalState from '../../../context/modal/useModalState';
import {
    EnabledChains,
    INSUFFICIENT_BALANCE,
    Validators,
} from '../../../config/constants';
import PercentageSelector from '../../../components/PercentageSelector';
import { formatUSD, validateErrors } from '../../../helpers';
import { Tokens } from '../../../config/constants';
import LeverageSlider from '../../../components/LeverageSlider';
import AssetInput from '../../../components/AssetInput';
import useTradeState from '../../../context/trade/useTradeState';
import useTokenDetails from '../../../hooks/web3/useTokenDetails';
import useUserBalance from '../../../hooks/web3/useUserBalance';
import useApproveToken from '../../../hooks/web3/useApproveToken';
import { useWeb3React } from '@web3-react/core';
import { config } from '../../../config/config';
import ExchangeSwapPreview from './ExchangeSwapPreview';
import useMinCollateral from '../../../hooks/web3/trade/useMinCollateral';
import useChartState from '../../../context/chart/useChartState';
import InfoPopUp from '../../../components/InfoPopUp';
import useToastState from '../../../context/toast/useToastState';

type ExchangeSwapContentProps = {
    children?: ReactNode;
    handleConfirm: () => void;
    validators: Validators[];
};

const MARKS = [
    {
        label: '1.1',
        index: 0,
    },
    {
        label: '15',
        index: 15,
    },
    {
        label: '30',
        index: 30,
    },
    {
        label: '45',
        index: 45,
    },
    {
        label: '60',
        index: 60,
    },
    {
        label: '75',
        index: 75,
    },
    {
        label: '90',
        index: 90,
    },
];

const ExchangeSwapContent: React.FC<ExchangeSwapContentProps> = ({
    children,
    handleConfirm,
    validators,
}) => {
    const { chainId, account } = useWeb3React();
    const { setToast } = useToastState();
    const { openModal, closeModal } = useModalState();
    const { setError } = useErrorState();
    const { collateral, leverage, setLeverage, setCollateral } = useTradeState();
    const { selectedTradePair } = useChartState();
    const { minCollateral } = useMinCollateral(selectedTradePair?.address);
    const [initialCollateral, setInitialCollateral] = useState(false);

    useEffect(() => {
        if (!collateral && minCollateral && !initialCollateral) {
            setCollateral(minCollateral);
            setInitialCollateral(true);
        }
    }, [initialCollateral, minCollateral, collateral, setCollateral]);

    const userBalance = useUserBalance(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { isApproved, approve } = useApproveToken(
        config.addresses[chainId as EnabledChains[number]]?.collateral,
        config.addresses[chainId as EnabledChains[number]]?.tradeManager,
        collateral
    );

    const defaultValidators: Validators[] = [
        {
            errorCondition: parseEther(collateral || '0').lt(
                parseEther(minCollateral || '0')
            ),
            message: `Mininum Collateral required is ${formatUSD(minCollateral || '0')}!`,
        },
        {
            errorCondition:
                !leverage ||
                parseEther(leverage).lt(
                    parseEther(
                        selectedTradePair?.minLeverage || config.minLeverage.toString()
                    )
                ),
            message: `Minimum Leverage required is ${selectedTradePair?.minLeverage}!`,
        },
        {
            errorCondition:
                !userBalance || userBalance.lt(parseUnits(collateral || '0', decimals)),
            message: INSUFFICIENT_BALANCE,
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setCollateral(preventOverflow(value, decimals));
    };

    const confirm = async () => {
        if (!userBalance) return;

        if (
            !validateErrors([...validators, ...defaultValidators], (message) =>
                setToast(
                    {
                        type: 'danger',
                        message,
                    },
                    message
                )
            )
        ) {
            return;
        }

        try {
            openModal('waitingModal');

            await handleConfirm();

            setCollateral('');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <div className='swap-content-wrapper'>
            <div className='swap-content'>
                <div className='collateral-container'>
                    <AssetInput
                        label='Collateral'
                        renderLabel={(label) => (
                            <InfoPopUp trigger={<span className='label'>{label}</span>}>
                                Minimum collateral required:{' '}
                                {minCollateral ? formatUSD(minCollateral) : '-'}
                            </InfoPopUp>
                        )}
                        sideLabel={
                            <>
                                <span>Balance:</span>&nbsp;
                                <span>
                                    {userBalance
                                        ? formatUSD(formatUnits(userBalance, decimals), {
                                              maxDecimals: 2,
                                              currency: '',
                                          })
                                        : '-'}
                                    &nbsp; USDC
                                </span>
                            </>
                        }
                        onSideLabelClick={() =>
                            userBalance
                                ? setCollateral(
                                      formatUSD(
                                          formatUnits(userBalance || '0', decimals),
                                          {
                                              currency: '',
                                              useGrouping: false,
                                              maxDecimals: 18,
                                          }
                                      )
                                  )
                                : undefined
                        }
                        asset={Tokens.USDC}
                        name='collateral'
                        value={collateral}
                        onChange={handleChange}
                    />
                    <PercentageSelector
                        currentValue={collateral}
                        totalValue={formatUnits(userBalance || '0', decimals)}
                        handleChange={(value) =>
                            setCollateral(preventOverflow(value, decimals))
                        }
                        decimals={decimals}
                        disableButtons={!userBalance}
                    />
                </div>
                {children}
                <LeverageSlider
                    minLeverage={selectedTradePair?.minLeverage}
                    value={leverage}
                    onChange={setLeverage}
                    marks={MARKS}
                />
                <ExchangeSwapPreview />
                {isApproved ? (
                    <Button
                        disabled={!collateral || parseEther(collateral).isZero()}
                        onClick={confirm}
                    >
                        Confirm
                    </Button>
                ) : (
                    <Button onClick={approve} disabled={!account}>
                        {account ? 'Approve' : 'Connect Wallet'}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default ExchangeSwapContent;
